import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, Loader2, AlertTriangle, Phone as PhoneIcon, Home } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { businessInfo } from "../mock";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_POLL_ATTEMPTS = 20;
const POLL_INTERVAL_MS = 3000;
const SHOW_SLOW_AFTER_ATTEMPTS = 8;

const BookingSuccess = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ phase: "checking", message: "" });
  const attempts = useRef(0);
  const timer = useRef(null);

  useEffect(() => {
    if (!sessionId) {
      setState({ phase: "error", message: "Missing payment session." });
      return;
    }

    const poll = async () => {
      attempts.current += 1;
      try {
        const { data } = await axios.get(`${API}/bookings/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState({ phase: "success", message: "" });
          return;
        }
        if (data.status === "expired" || data.payment_status === "failed") {
          setState({ phase: "error", message: "Payment session expired or failed." });
          return;
        }
        // Show "almost there" message after a few attempts but KEEP POLLING in the background
        if (attempts.current === SHOW_SLOW_AFTER_ATTEMPTS) {
          setState({
            phase: "slow",
            message:
              "Still confirming with Stripe — this can take up to a minute. A confirmation email will land in your inbox as soon as it clears.",
          });
        }
        if (attempts.current >= MAX_POLL_ATTEMPTS) {
          // Final timeout — payment may still go through via webhook; tell customer they'll be contacted
          setState({
            phase: "slow",
            message:
              "Payment is still processing. You'll receive a confirmation email shortly, or Shane will call if anything's missing.",
          });
          return;
        }
        timer.current = setTimeout(poll, POLL_INTERVAL_MS);
      } catch (err) {
        if (attempts.current >= MAX_POLL_ATTEMPTS) {
          setState({
            phase: "slow",
            message: "Couldn't confirm right away. Shane will call if anything's missing.",
          });
          return;
        }
        timer.current = setTimeout(poll, POLL_INTERVAL_MS);
      }
    };

    poll();
    return () => timer.current && clearTimeout(timer.current);
  }, [sessionId]);

  return (
    <div className="text-left bg-black text-white min-h-screen">
      <Header />
      <main className="pt-28 pb-16">
        <div className="max-w-2xl mx-auto px-5 lg:px-8 text-center">
          {state.phase === "checking" && (
            <Status
              icon={<Loader2 className="w-16 h-16 text-yellow-400 animate-spin" />}
              title="Confirming your payment…"
              body="Hang tight — this usually takes 5–10 seconds."
            />
          )}
          {state.phase === "success" && (
            <Status
              icon={
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-green-500 to-green-700 flex items-center justify-center shadow-lg shadow-green-900/50">
                  <CheckCircle2 className="w-12 h-12 text-white" strokeWidth={2.2} />
                </div>
              }
              title="Booking Confirmed!"
              body={
                <>
                  Your deposit is paid and your appointment is reserved.
                  <br />
                  <span className="text-zinc-400">Shane will call <strong className="text-white">{businessInfo.phone}</strong> to confirm the exact time. A confirmation email is on its way.</span>
                </>
              }
              accent="green"
            />
          )}
          {state.phase === "slow" && (
            <Status
              icon={<Loader2 className="w-16 h-16 text-yellow-400" />}
              title="Almost there…"
              body={state.message}
            />
          )}
          {state.phase === "error" && (
            <Status
              icon={<AlertTriangle className="w-16 h-16 text-red-500" />}
              title="Hmm, something didn't go through"
              body={(
                <>
                  {state.message}
                  <br />
                  Please call <a href={`tel:${businessInfo.phoneRaw}`} className="text-red-400 font-semibold">{businessInfo.phone}</a> and Shane will sort it out.
                </>
              )}
              accent="red"
            />
          )}

          <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
            <Button asChild className="bg-brand-red text-white font-semibold h-12 px-6">
              <a href={`tel:${businessInfo.phoneRaw}`}>
                <PhoneIcon className="w-4 h-4 mr-2" />
                Call Shane
              </a>
            </Button>
            <Button asChild variant="outline" className="h-12 px-6 bg-transparent border-zinc-700 text-zinc-200 hover:bg-zinc-900">
              <Link to="/">
                <Home className="w-4 h-4 mr-2" />
                Back to Home
              </Link>
            </Button>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

const Status = ({ icon, title, body, accent }) => (
  <div className="flex flex-col items-center gap-5">
    {icon}
    <h1 className={`font-extrabold text-3xl lg:text-4xl tracking-tight ${accent === "green" ? "text-white" : accent === "red" ? "text-white" : "text-white"}`}>
      {title}
    </h1>
    <p className="text-zinc-300 text-base lg:text-lg leading-relaxed max-w-lg">{body}</p>
  </div>
);

export default BookingSuccess;
